/**
 * LabelConfigPanel
 *
 * UI component for configuring feature labeling.
 * Allows users to select a label field, customize font/size/color/halo,
 * choose placement options, and create expression-based labels.
 *
 * **Validates: Requirements 43.1, 43.2, 43.3, 43.4, 43.5, 43.6**
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import { Label, FormatSize } from '@mui/icons-material';
import type { LabelConfig, LabelPlacement } from '../types/symbology';
import { DEFAULT_LABEL_CONFIG } from '../types/symbology';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LabelConfigPanelProps {
  /** Available attribute field names. */
  fields: string[];
  /** Current label configuration (if any). */
  currentConfig?: LabelConfig;
  /** Geometry type of the layer (affects placement options). */
  geometryType: 'Point' | 'LineString' | 'Polygon';
  /** Called when the user applies the label configuration. */
  onApply: (config: LabelConfig) => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FONT_FAMILIES = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'sans-serif',
  'serif',
  'monospace',
];

const PLACEMENT_OPTIONS: {
  value: LabelPlacement;
  label: string;
  description: string;
  geometryTypes: string[];
}[] = [
  {
    value: 'point',
    label: 'Point',
    description: 'Label at feature centroid',
    geometryTypes: ['Point', 'Polygon'],
  },
  {
    value: 'line',
    label: 'Along Line',
    description: 'Label follows line geometry',
    geometryTypes: ['LineString'],
  },
  {
    value: 'centroid',
    label: 'Centroid',
    description: 'Label at polygon centroid',
    geometryTypes: ['Polygon'],
  },
];

// ---------------------------------------------------------------------------
// Color input helper
// ---------------------------------------------------------------------------

const isValidHex = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
}

const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Tooltip title={`Pick ${label.toLowerCase()}`}>
      <Box
        component="label"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '4px',
          border: '2px solid',
          borderColor: 'divider',
          backgroundColor: isValidHex(value) ? value : '#cccccc',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <input
          type="color"
          value={isValidHex(value) ? value : '#cccccc'}
          onChange={(e) => onChange(e.target.value)}
          style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
          aria-label={`${label} color picker`}
        />
      </Box>
    </Tooltip>
    <TextField
      label={label}
      value={value}
      onChange={(e) => {
        const v = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
        if (isValidHex(v)) onChange(v);
      }}
      size="small"
      fullWidth
    />
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const LabelConfigPanel: React.FC<LabelConfigPanelProps> = ({
  fields,
  currentConfig,
  geometryType,
  onApply,
  onCancel,
}) => {
  const [config, setConfig] = useState<LabelConfig>(currentConfig || { ...DEFAULT_LABEL_CONFIG });
  const [useExpression, setUseExpression] = useState<boolean>(!!currentConfig?.expression);

  // Available placement options based on geometry type
  const availablePlacements = PLACEMENT_OPTIONS.filter((p) =>
    p.geometryTypes.includes(geometryType)
  );

  // Update config field
  const updateConfig = useCallback(<K extends keyof LabelConfig>(key: K, value: LabelConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle expression toggle
  const handleExpressionToggle = useCallback((enabled: boolean) => {
    setUseExpression(enabled);
    if (!enabled) {
      setConfig((prev) => ({ ...prev, expression: undefined }));
    }
  }, []);

  // Apply
  const handleApply = useCallback(() => {
    onApply(config);
  }, [config, onApply]);

  // Label preview text
  const previewText = useCallback(() => {
    if (config.expression) {
      return config.expression.replace(/\{([^}]+)\}/g, (_, field) => `[${field}]`);
    }
    return config.field ? `[${config.field}]` : 'No field selected';
  }, [config.expression, config.field]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Label color="primary" fontSize="small" />
        Feature Labeling
      </Typography>

      {/* Enable/disable labeling */}
      <FormControlLabel
        control={
          <Switch
            checked={config.enabled}
            onChange={(e) => updateConfig('enabled', e.target.checked)}
            inputProps={{ 'aria-label': 'Enable labeling' }}
          />
        }
        label="Enable Labels"
        sx={{ mb: 2 }}
      />

      {config.enabled && (
        <Stack spacing={2}>
          {/* Field selector or expression */}
          <FormControlLabel
            control={
              <Switch
                checked={useExpression}
                onChange={(e) => handleExpressionToggle(e.target.checked)}
                size="small"
                inputProps={{ 'aria-label': 'Use expression-based label' }}
              />
            }
            label={<Typography variant="body2">Expression-based label</Typography>}
          />

          {!useExpression ? (
            <FormControl fullWidth size="small">
              <InputLabel id="label-field-label">Label Field</InputLabel>
              <Select
                labelId="label-field-label"
                value={config.field}
                label="Label Field"
                onChange={(e) => updateConfig('field', e.target.value)}
                inputProps={{ 'aria-label': 'Select label field' }}
              >
                {fields.map((field) => (
                  <MenuItem key={field} value={field}>
                    {field}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Label Expression"
                value={config.expression || ''}
                onChange={(e) => updateConfig('expression', e.target.value)}
                placeholder="{field1} - {field2}"
                helperText="Use {fieldName} to reference attribute values"
              />
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {fields.map((field) => (
                  <Chip
                    key={field}
                    label={field}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const expr = config.expression || '';
                      updateConfig('expression', `${expr}{${field}}`);
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider />

          {/* Font settings */}
          <Typography variant="subtitle2" color="text.secondary">
            Font Style
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="label-font-label">Font Family</InputLabel>
            <Select
              labelId="label-font-label"
              value={config.font}
              label="Font Family"
              onChange={(e) => updateConfig('font', e.target.value)}
              inputProps={{ 'aria-label': 'Select font family' }}
            >
              {FONT_FAMILIES.map((font) => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                  {font}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" gutterBottom>
              Font Size: {config.fontSize}px
            </Typography>
            <Slider
              value={config.fontSize}
              onChange={(_, v) => updateConfig('fontSize', v as number)}
              min={6}
              max={48}
              step={1}
              marks={[
                { value: 6, label: '6' },
                { value: 12, label: '12' },
                { value: 24, label: '24' },
                { value: 48, label: '48' },
              ]}
              valueLabelDisplay="auto"
              aria-label="Font size"
            />
          </Box>

          <ColorField
            label="Text Color"
            value={config.color}
            onChange={(color) => updateConfig('color', color)}
          />

          <Divider />

          {/* Halo settings */}
          <Typography variant="subtitle2" color="text.secondary">
            Text Halo (Outline)
          </Typography>

          <ColorField
            label="Halo Color"
            value={config.haloColor || '#ffffff'}
            onChange={(color) => updateConfig('haloColor', color)}
          />

          <Box>
            <Typography variant="body2" gutterBottom>
              Halo Width: {config.haloWidth || 2}px
            </Typography>
            <Slider
              value={config.haloWidth || 2}
              onChange={(_, v) => updateConfig('haloWidth', v as number)}
              min={0}
              max={10}
              step={0.5}
              marks={[
                { value: 0, label: '0' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
              ]}
              valueLabelDisplay="auto"
              aria-label="Halo width"
            />
          </Box>

          <Divider />

          {/* Placement */}
          <Typography variant="subtitle2" color="text.secondary">
            Label Placement
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="label-placement-label">Placement</InputLabel>
            <Select
              labelId="label-placement-label"
              value={config.placement}
              label="Placement"
              onChange={(e) => updateConfig('placement', e.target.value as LabelPlacement)}
              inputProps={{ 'aria-label': 'Select label placement' }}
            >
              {availablePlacements.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  <Box>
                    <Typography variant="body2">{p.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Declutter option */}
          <FormControlLabel
            control={
              <Switch
                checked={config.declutter ?? true}
                onChange={(e) => updateConfig('declutter', e.target.checked)}
                size="small"
                inputProps={{ 'aria-label': 'Enable declutter to avoid overlaps' }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Avoid Label Overlaps</Typography>
                <Typography variant="caption" color="text.secondary">
                  Automatically hide overlapping labels
                </Typography>
              </Box>
            }
          />

          {/* Preview */}
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block' }}
            >
              Preview
            </Typography>
            <Typography
              sx={{
                fontFamily: config.font,
                fontSize: `${Math.min(config.fontSize, 24)}px`,
                color: config.color,
                textShadow: config.haloColor
                  ? `0 0 ${config.haloWidth || 2}px ${config.haloColor}, 0 0 ${config.haloWidth || 2}px ${config.haloColor}`
                  : 'none',
              }}
            >
              {previewText()}
            </Typography>
          </Paper>
        </Stack>
      )}

      {/* Actions */}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </Stack>
    </Box>
  );
};

export default LabelConfigPanel;
